// Analytics and Error Reporting Setup

// ============================================
// SENTRY (Error Monitoring)
// ============================================

import { captureExceptionLazy, initSentryLazy } from './sentryClient';

// ============================================
// POSTHOG (User Analytics)
// ============================================

// Uncomment and add your PostHog key once you have it:
// import posthog from 'posthog-js';

// posthog.init('YOUR_POSTHOG_KEY_HERE', {
//   api_host: 'https://app.posthog.com',
//   person_profiles: 'identified_only',
//   capture_pageview: false, // We'll capture manually
//   capture_pageleave: true,
// });

// ============================================
// ANALYTICS FUNCTIONS
// ============================================

export const analytics = {
  // Track page views
  trackPageView: (pageName: string, properties?: Record<string, any>) => {
    console.log(`📊 Page View: ${pageName}`, properties);
    
    // Uncomment once PostHog is set up:
    // posthog.capture('$pageview', {
    //   page: pageName,
    //   ...properties
    // });
  },

  // Track user actions
  trackEvent: (eventName: string, properties?: Record<string, any>) => {
    console.log(`📊 Event: ${eventName}`, properties);
    
    // Uncomment once PostHog is set up:
    // posthog.capture(eventName, properties);
  },

  // Track errors
  trackError: (error: Error, context?: Record<string, any>) => {
    console.error('🚨 Error tracked:', error, context);
    
    // Lazy-init Sentry to keep it off the landing bundle
    captureExceptionLazy(error, context);
  },

  // Identify user
  identifyUser: (userId: string, properties?: Record<string, any>) => {
    console.log(`👤 User identified: ${userId}`, properties);
    
    // Uncomment once PostHog is set up:
    // posthog.identify(userId, properties);
  },

  // Set user properties
  setUserProperties: (properties: Record<string, any>) => {
    console.log('👤 User properties set:', properties);
    
    // Uncomment once PostHog is set up:
    // posthog.people.set(properties);
  }
};

// Ensure Sentry is ready when analytics is explicitly loaded (after interaction)
initSentryLazy().catch((err) => {
  console.warn('Sentry initialization failed (lazy):', err);
});

// ============================================
// ERROR BOUNDARY COMPONENT
// ============================================

import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<
  React.PropsWithChildren<{}>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<{}>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Track error
    analytics.trackError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });
  }

  render() {
    if (this.state.hasError) {
      return React.createElement('div', {
        style: {
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: 'var(--color-card)',
          borderRadius: '8px',
      border: '1px solid var(--color-danger)',
          margin: '1rem'
        }
      }, [
        React.createElement('h3', {
          key: 'title',
      style: { color: '#e4574d', marginBottom: '1rem' }
        }, 'Something went wrong'),
        React.createElement('p', {
          key: 'message',
          style: { color: '#999', marginBottom: '1rem' }
        }, 'We\'ve been notified about this error and are working to fix it.'),
        React.createElement('button', {
          key: 'reload',
          onClick: () => window.location.reload(),
          style: {
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--color-accent)',
            color: '#000',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }
        }, 'Reload Page')
      ]);
    }

    return this.props.children;
  }
}

// ============================================
// COMMON TRACKING EVENTS
// ============================================

export const trackEvents = {
  // User actions
  userSignUp: (method: string) => analytics.trackEvent('user_signup', { method }),
  userLogin: (method: string) => analytics.trackEvent('user_login', { method }),
  userLogout: () => analytics.trackEvent('user_logout'),
  
  // Event actions
  eventCreated: (eventId: string, isPublic: boolean) => 
    analytics.trackEvent('event_created', { event_id: eventId, is_public: isPublic }),
  eventJoined: (eventId: string) => 
    analytics.trackEvent('event_joined', { event_id: eventId }),
  eventLeft: (eventId: string) => 
    analytics.trackEvent('event_left', { event_id: eventId }),
  
  // Check-in actions
  checkInCreated: (hasPhoto: boolean, venueId?: string) => 
    analytics.trackEvent('checkin_created', { venue_id: venueId, has_photo: hasPhoto }),
  
  // Map actions
  mapViewed: (hasLocation: boolean) => 
    analytics.trackEvent('map_viewed', { has_location: hasLocation }),
  locationShared: () => analytics.trackEvent('location_shared'),
  
  // Profile actions
  profileUpdated: (fields: string[]) => 
    analytics.trackEvent('profile_updated', { fields }),
  connectionSent: () => analytics.trackEvent('connection_sent'),
  connectionAccepted: () => analytics.trackEvent('connection_accepted'),
  
  // Performance
  pageLoadTime: (page: string, loadTime: number) => 
    analytics.trackEvent('page_load_time', { page, load_time: loadTime }),
  apiCallTime: (endpoint: string, duration: number) => 
    analytics.trackEvent('api_call_time', { endpoint, duration })
};

// ============================================
// SETUP INSTRUCTIONS
// ============================================

/*
SETUP INSTRUCTIONS:

1. SENTRY SETUP:
   - Go to sentry.io and create a free account
   - Create a new project (React)
   - Copy your DSN
   - Run: npm install @sentry/react
   - Uncomment the Sentry code above and add your DSN

2. POSTHOG SETUP:
   - Go to posthog.com and create a free account
   - Get your project API key
   - Run: npm install posthog-js
   - Uncomment the PostHog code above and add your key

3. USAGE:
   - Import analytics from this file
   - Use analytics.trackEvent() for custom events
   - Use trackEvents.* for common events
   - Wrap your app with <ErrorBoundary> for error tracking

4. DASHBOARDS:
   - Sentry: sentry.io (error monitoring)
   - PostHog: posthog.com (user analytics)
   - Both are private - users never see them
*/
